import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { environment } from '@app/environment/environment';
import { FileUploadControl } from '@app/models/form.models';
import { FileResponse } from '@app/models/shared.models';
import { Item } from '@app/ui-components/card-array/card-array.component';
import { ImgGalleryComponent } from '@app/ui-components/img-gallery/img-gallery.component';
import { TableColumn } from '@app/ui-components/table/table.component';
import { ValidatedCheckboxComponent } from '@app/ui-components/validated-checkbox/validated-checkbox.component';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import { ValidatedTextEditorComponent } from '@app/ui-components/validated-text-editor/validated-text-editor.component';
import { UploadFilesComponent } from '../../components/common/upload-files/upload-files.component';
import { ValidatedCurrencyInputComponent } from '../../components/common/validated-currency-input/validated-currency-input.component';

import GLightbox from 'glightbox';

type RowData = {
  name: string;
  email: string;
  age: number;
};

@Component({
  selector: 'app-demo-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ValidatedCheckboxComponent,
    ValidatedInputComponent,
    UploadFilesComponent,
    ValidatedCurrencyInputComponent,
    ImgGalleryComponent,
    ValidatedTextEditorComponent,
  ],
  templateUrl: './demo-page.component.html',
  styleUrl: './demo-page.component.scss',
})
export class DemoPageComponent implements AfterViewInit {
  protected disabledDemo: FormGroup<any>;
  cardArrayItems: Item[];
  protected columns: TableColumn<RowData>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      sortFn: (a, b) => a.name.localeCompare(b.name),
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      sortFn: (a, b) => a.email.localeCompare(b.email),
    },
    {
      key: 'age',
      label: 'Age',
      sortable: true,
      sortFn: (a, b) => a.age - b.age,
    },
  ];

  protected rowsData = [
    { name: 'Anna', email: 'anna@example.com', age: 18 },
    { name: 'Homer', email: 'homer@example.com', age: 35 },
    { name: 'Oscar', email: 'oscar@example.com', age: 52 },
    { name: 'Emily', email: 'emily@example.com', age: 30 },
    { name: 'Jara', email: 'jara@example.com', age: 25 },
    { name: 'Clark', email: 'clark@example.com', age: 39 },
    { name: 'Jennifer', email: 'jennifer@example.com', age: 52 },
    { name: 'Tony', email: 'tony@example.com', age: 30 },
    { name: 'Tom', email: 'tom@example.com', age: 25 },
    { name: 'Michael', email: 'michael@example.com', age: 39 },
    { name: 'Antony', email: 'antony@example.com', age: 39 },
    { name: 'Raymond', email: 'raymond@example.com', age: 52 },
    { name: 'Marie', email: 'marie@example.com', age: 30 },
    { name: 'Cohen', email: 'cohen@example.com', age: 25 },
    { name: 'Rowen', email: 'rowen@example.com', age: 39 },
    { name: 'Tony', email: 'tony@example.com', age: 30 },
    { name: 'Tom', email: 'tom@example.com', age: 25 },
    { name: 'Michael', email: 'michael@example.com', age: 39 },
    { name: 'Antony', email: 'antony@example.com', age: 39 },
    { name: 'Raymond', email: 'raymond@example.com', age: 52 },
    { name: 'Marie', email: 'marie@example.com', age: 30 },
    { name: 'Cohen', email: 'cohen@example.com', age: 25 },
    { name: 'Rowen', email: 'rowen@example.com', age: 39 },
    { name: 'Tony', email: 'tony@example.com', age: 30 },
    { name: 'Tom', email: 'tom@example.com', age: 25 },
    { name: 'Michael', email: 'michael@example.com', age: 39 },
    { name: 'Antony', email: 'antony@example.com', age: 39 },
    { name: 'Raymond', email: 'raymond@example.com', age: 52 },
    { name: 'Marie', email: 'marie@example.com', age: 30 },
    { name: 'Cohen', email: 'cohen@example.com', age: 25 },
    { name: 'Rowen', email: 'rowen@example.com', age: 39 },
    { name: 'Tony', email: 'tony@example.com', age: 30 },
    { name: 'Tom', email: 'tom@example.com', age: 25 },
    { name: 'Michael', email: 'michael@example.com', age: 39 },
    { name: 'Antony', email: 'antony@example.com', age: 39 },
    { name: 'Raymond', email: 'raymond@example.com', age: 52 },
    { name: 'Marie', email: 'marie@example.com', age: 30 },
    { name: 'Cohen', email: 'cohen@example.com', age: 25 },
    { name: 'Rowen', email: 'rowen@example.com', age: 39 },
  ];

  protected checboxData: FormControl<boolean>;
  protected checboxData2: FormControl<boolean | null>;
  protected inputData: FormControl<string | null>;
  protected formControls: FormArray<FormGroup<FileUploadControl>>;
  currencyInput: FormControl<number>;
  protected textEditor: FormControl<string>;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
  ) {
    this.disabledDemo = fb.nonNullable.group({
      disabledFamilyName: fb.nonNullable.control('Test1'),
      disabledMiddleName: fb.nonNullable.control('Test2'),
      disabledGivenName: fb.nonNullable.control('Test3'),
    });
    this.disabledDemo.disable();
    this.cardArrayItems = [
      {
        title: 'Item 1',
        formGroup: fb.group({
          familyName: new FormControl(''),
          middleName: new FormControl(),
          givenName: new FormControl(),
        }),
      },
    ];

    this.checboxData = fb.nonNullable.control(false, [Validators.requiredTrue]);
    this.checboxData2 = fb.control(null, [Validators.required]);
    this.inputData = fb.control<string | null>(null, [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(6),
    ]);

    this.currencyInput = fb.nonNullable.control(0, [Validators.required]);

    this.formControls = this.getFormControls();

    this.http.get(environment.apiUrl + '');

    this.textEditor = fb.nonNullable.control('', [Validators.required]);
  }
  ngAfterViewInit(): void {
    GLightbox({});
  }

  removeItem($event: number) {
    this.cardArrayItems.splice($event, 1);
  }

  addItem() {
    this.cardArrayItems.push({
      title: 'Item ' + (this.cardArrayItems.length + 1),
      formGroup: this.fb.group({
        familyName: new FormControl(),
        middleName: new FormControl(),
        givenName: new FormControl(),
      }),
    });
  }

  validate() {
    this.checboxData.markAsTouched();
    this.checboxData2.markAsTouched();
    this.checboxData.updateValueAndValidity();
    this.checboxData2.updateValueAndValidity();
    this.inputData.markAsTouched();
    this.inputData.updateValueAndValidity();
  }

  getFormControls(): FormArray<FormGroup<FileUploadControl>> {
    return this.fb.array([
      this.fb.group({
        fileType: this.fb.control(null),
        id: this.fb.nonNullable.control(''),
        isPublic: this.fb.nonNullable.control(false),
        title: this.fb.nonNullable.control('', [Validators.required]),
        description: this.fb.nonNullable.control('', [Validators.required]),
        file: this.fb.control<File | null>(null, [Validators.required]),
      }),
    ]) as FormArray<FormGroup<FileUploadControl>>;
  }

  createNewFileGroup() {
    this.formControls.push(
      this.fb.nonNullable.group({
        fileType: this.fb.control<number | null>(null),
        id: this.fb.nonNullable.control<string>(''),
        isPublic: this.fb.nonNullable.control(false),
        title: this.fb.control<string | null>(null, [Validators.required]),
        description: this.fb.control<string | null>(null, [
          Validators.required,
        ]),
        file: this.fb.control<File | null>(null, [Validators.required]),
      }),
    );
  }

  fileRemoved($event: number) {
    this.formControls.removeAt($event);
  }

  private getImgMockData(amount: number = 5): FileResponse[] {
    const imgData: FileResponse[] = [];
    for (let i = 0; i < amount; i++) {
      imgData.push({
        id: '20cf5d22-0ffc-4e70-7153-08dd92607ce5',
        name: '2025-05-03 19_48_46-stock photo of project bujilding at DuckDuckGo.png',
        description:
          '2025-05-03 19_48_46-stock photo of project bujilding at DuckDuckGo.png',
        fileType: 1,
        fileName: null,
        originalFileName: null,
        blobId: null,
        extension: null,
        contentType: 'image/png',
        size: 1071088,
      });
    }
    return imgData;
  }

  images: FileResponse[] = this.getImgMockData(5);
  manyImage: FileResponse[] = this.getImgMockData(15);
  imagesWithNotFoundId: FileResponse[] = [
    ...this.getImgMockData(3),
    {
      id: '0',
      name: '2025-05-03 19_48_46-stock photo of project bujilding at DuckDuckGo.png',
      description:
        '2025-05-03 19_48_46-stock photo of project bujilding at DuckDuckGo.png',
      fileType: 1,
      fileName: null,
      originalFileName: null,
      blobId: null,
      extension: null,
      contentType: 'image/png',
      size: 1071088,
    },
    {
      id: '0',
      name: '2025-05-03 19_48_46-stock photo of project bujilding at DuckDuckGo.png',
      description:
        '2025-05-03 19_48_46-stock photo of project bujilding at DuckDuckGo.png',
      fileType: 1,
      fileName: null,
      originalFileName: null,
      blobId: null,
      extension: null,
      contentType: 'image/png',
      size: 1071088,
    },
    {
      id: '0',
      name: '2025-05-03 19_48_46-stock photo of project bujilding at DuckDuckGo.png',
      description:
        '2025-05-03 19_48_46-stock photo of project bujilding at DuckDuckGo.png',
      fileType: 1,
      fileName: null,
      originalFileName: null,
      blobId: null,
      extension: null,
      contentType: 'image/png',
      size: 1071088,
    },
  ];

  galleries = [['assets/img/images/Illustration.png']];
}
